<template>
  <div class="payment-details">
    <PaymentDetailsCard @openModal="openModal" :canEdit="allowEditPaymentMethod" :isUpdated="isUpdateAttempt" />
    <!-- Payment Details "Modal"-->
    <div class="modal-blurred-background" :class="{ hidden: !isModalOpen }">
      <div class="payment-details-modal" data-test="payment-details-modal">
        <header>
          <h1 class="title" data-test="change-payment-details-heading">Change Payment Details</h1>
          <button class="close-button" tabindex="0" @click="closeModal">
            <Close fill="currentColor" />
          </button>
        </header>
        <form id="checkout-form" autocomplete="off" @submit.prevent novalidate>
          <RecurlyBillingInputGroup v-model:form="form" @selectPaymentMethod="updatePaymentMethod" :isUpdateAttempt="isUpdateAttempt" :paymentRequired="true">
            <RecurlyCreditCardFields v-if="paymentMethod === PaymentMethodType.creditCard" ref="creditCardFields" />
          </RecurlyBillingInputGroup>
          <CashAppContainer v-if="paymentMethod === PaymentMethodType.cashApp" container-id="cashapp-container-modal" />

          <ScreenNextSectionBtn text="Update Payment Details" hide-enter :disabled="!formIsValid" @navigateClick="updateBillingInfo" />
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect, nextTick, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useGtm, useAmplitude } from '@outputinc/o-analytics'
import PaymentDetailsCard from '@/components/shared/payment-details/PaymentDetailsCard.vue'
import ScreenNextSectionBtn from '@/components/shared/screens/ScreenNextSectionBtn.vue'
import RecurlyBillingInputGroup from '@/components/shared/recurly/RecurlyBillingInputGroup.vue'
import RecurlyCreditCardFields from '@/components/shared/recurly/RecurlyCreditCardFields.vue'
import CashAppContainer from '@/components/shared/recurly/CashAppContainer.vue'
import Close from '@/components/shared/svg/Close.vue'
import { useErrorsStore } from '@/store/ErrorsStore'
import { PaymentMethodType } from '@/types'
import { useRecurlyStore } from '@/store/RecurlyStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useUserStore } from '@/store/UserStore'
import { usePlanStore } from '@/store/PlanStore'
import { useOfferStore } from '@/store/OfferStore'
import { useScreenStore } from '@/store/ScreenStore'
const { track: gtmTrackEvent, Events: GtmEvents } = useGtm()
const { track, Events } = useAmplitude()
const errorsStore = useErrorsStore()
const { clear: clearErrors } = errorsStore
const recurlyStore = useRecurlyStore()
const { setPaymentMethod, getToken: getRecurlyToken, refreshCashApp, registerCashAppRedirectListener } = recurlyStore
const { paymentMethod } = storeToRefs(recurlyStore)
const userStore = useUserStore()
const { country: userCountry } = storeToRefs(userStore)
const checkoutStore = useCheckoutStore()
const { setCheckoutProperty } = checkoutStore
const planStore = usePlanStore()
const { selectedPlanProduct, selectedPlanCode } = storeToRefs(planStore)
const offerStore = useOfferStore()
const { activeOffer, activeOfferAllowPaymentUpdates } = storeToRefs(offerStore)
const screenStore = useScreenStore()
const { setBusy } = screenStore

const isModalOpen = ref(false)
const isUpdateAttempt = ref(false)

const creditCardFields = ref<InstanceType<typeof RecurlyCreditCardFields>>()

const form = ref({
  firstName: '',
  lastName: '',
  zipCode: '',
  country: userCountry.value || 'US'
})

const allowEditPaymentMethod = computed(() => (activeOffer.value ? activeOfferAllowPaymentUpdates.value : true))

const inputsAreValid = computed(() => Object.values(form.value).every(value => value.trim() !== ''))

const formIsValid = computed(() => {
  if (paymentMethod.value === PaymentMethodType.creditCard) {
    return inputsAreValid.value && creditCardFields.value?.isValid
  }
  return inputsAreValid.value
})

const updatePaymentMethod = (value: string) => {
  setPaymentMethod(value)
  track(Events.CHECKOUT_SUBSCRIPTION_PAYMENT_METHOD_SELECT, { paymentMethod: value, planCode: selectedPlanCode.value, products: selectedPlanProduct.value })
  gtmTrackEvent(GtmEvents.CHECKOUT_SUBSCRIPTION_PAYMENT_METHOD_SELECT, { paymentMethod: value, products: selectedPlanProduct.value })
}

const updateBillingInfo = async () => {
  isUpdateAttempt.value = true
  clearErrors()

  const { firstName, lastName, zipCode: postalCode, country } = form.value

  const checkoutPayload = {
    firstName,
    lastName,
    billingAddress: {
      postalCode,
      country
    }
  }

  if (formIsValid.value) {
    setCheckoutProperty(checkoutPayload)
    const tokenized = await getRecurlyToken()
    if (tokenized) {
      isModalOpen.value = false
    }
  }

  setBusy(false)
}

const resetModalState = () => {
  form.value = {
    firstName: '',
    lastName: '',
    zipCode: '',
    country: userCountry.value || 'US'
  }
  isUpdateAttempt.value = false
  clearErrors()
}

const openModal = async () => {
  resetModalState()
  isModalOpen.value = true
  // Adyen's Cash App widget enters a terminal state after each submit and stops responding on
  // subsequent attempts. Rebuild it whenever the modal opens with Cash App selected so the
  // user can retry.
  if (paymentMethod.value === PaymentMethodType.cashApp) {
    await nextTick()
    refreshCashApp('#cashapp-container-modal')
  }
}

const closeModal = () => {
  isModalOpen.value = false
}

watchEffect(() => {
  if (formIsValid.value) {
    clearErrors()
  }
})

// On mobile, Cash App Pay deep-links out to the Cash App app and returns the browser to our
// checkout route with `?cash_request_id=<id>` as a full page reload. The Recurly widget reads that
// URL param during its async start() and auto-fires the token event with the new tokenId. The
// catch: if we attach the token listener AFTER start() finishes (the natural place — when the
// instance is "ready"), the event has already fired into a void and the new tokenId never lands
// in our store. The subscription submit then goes out with empty billingInfo and lands in Recurly
// without a payment method.
//
// Fix: pre-register the token listener in the store BEFORE CashAppContainer mounts. initCashApp
// picks it up and attaches it before calling start(), so the auto-fired token event is captured.
onMounted(async () => {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('cash_request_id')) return

  const tokenPromise = registerCashAppRedirectListener()

  setPaymentMethod(PaymentMethodType.cashApp)
  isModalOpen.value = true

  const tokenId = await tokenPromise
  if (tokenId) {
    isModalOpen.value = false
  }

  // Strip the redirect param so a refresh/back-nav doesn't re-trigger this flow.
  url.searchParams.delete('cash_request_id')
  window.history.replaceState({}, '', url.toString())
})
</script>

<style scoped lang="scss">
.payment-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

// Centering is done with flexbox on the backdrop instead of `transform` on the modal because
// `transform` on an ancestor creates a containing block for `position: fixed` descendants. The
// Adyen Cash App Pay dialog (rendered into a shadow root with `fixed inset-0`) was getting
// captured by the modal and rendering low instead of overlaying the viewport.
.modal-blurred-background {
  left: 0;
  top: 0;
  position: fixed;
  background-color: rgba(16, 16, 16, 0.6);
  backdrop-filter: blur(4px);
  z-index: 999;
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @include media-breakpoint(sm) {
    align-items: center;
  }

  // Scoped-CSS specificity beats the global `.hidden` utility, so we have to handle the hidden
  // state explicitly inside the scoped block instead of relying on Tailwind's class alone.
  &.hidden {
    display: none;
  }
}

.payment-details-modal {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border-radius: 12px;
  box-shadow: 0px 2px 8px 8px rgba(255, 255, 255, 0.08);
  position: relative;
  width: 100%;
  max-width: 700px;
  padding: $spacer-3 0;

  // Cap the modal to the viewport and lay it out as a flex column so the form owns the scroll.
  // Without a bounded height the modal grows to its content height; on mobile (where the backdrop
  // pins it to the top) the submit button falls below the fold with no scroll container and becomes
  // unreachable — for real users and for Playwright alike. WEB-7801.
  // The 100vh line is an intentional fallback for browsers without dvh support — not a duplicate.
  max-height: 100vh;
  max-height: 100dvh;
  display: flex;
  flex-direction: column;

  header {
    flex: 0 0 auto;
    padding: 0 $spacer-4 $spacer-2 $spacer-4;
    display: flex;
    justify-content: space-between;

    .title {
      font-size: $fs6;
      line-height: $line-height-7;
      margin-bottom: 0;
    }

    .close-button {
      padding: 0;
      border: 2px solid $gray-300;
      border-radius: 50%;
      margin-top: $spacer-2;
      height: 32px;
      width: 32px;
      color: $gray-300;

      &:hover {
        background-color: var(--color-bg-secondary);
      }
    }
  }
}

#checkout-form {
  flex: 1 1 auto;
  min-height: 0; // let the flex child shrink below its content height so overflow-y can scroll
  overflow-y: auto;
  padding: $spacer-2 $spacer-5;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
